<template>
  <div class="restaurant-menu-items-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="restaurant-menu-items-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="page-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="title-icon">
                    <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
                  </svg>
                  Menu Items
                </h1>
                <p class="page-subtitle">Manage your restaurant menu items</p>
              </div>
              <div class="header-actions">
                <button @click="showAddModal = true" class="btn-primary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                  </svg>
                  Add Menu Item
                </button>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="filters-section">
            <div class="filter-group">
              <label class="filter-label">Category</label>
              <select v-model="filters.categoryId" class="filter-select" @change="loadMenuItems">
                <option :value="null">All Categories</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Status</label>
              <select v-model="filters.available" class="filter-select" @change="loadMenuItems">
                <option :value="null">All Status</option>
                <option :value="true">Available</option>
                <option :value="false">Out of Stock</option>
              </select>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading menu items...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="menuItems.length === 0" class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
              <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
            </svg>
            <h3>No Menu Items Yet</h3>
            <p>Add items to your menu so customers can start ordering!</p>
            <button @click="showAddModal = true" class="btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
              </svg>
              Add First Item
            </button>
          </div>

          <!-- Menu Items Grid -->
          <div v-else class="menu-items-grid">
            <div
              v-for="item in menuItems"
              :key="item.id"
              class="menu-item-card"
            >
              <div v-if="item.image_url" class="item-image">
                <img :src="item.image_url" :alt="item.name" />
                <span :class="['availability-badge', { available: item.available }]">
                  {{ item.available ? 'Available' : 'Out of Stock' }}
                </span>
              </div>

              <div class="item-content">
                <div class="item-header">
                  <h4 class="item-name">{{ item.name }}</h4>
                  <span class="item-price">{{ item.price }} EGP</span>
                </div>

                <p v-if="item.description" class="item-description">
                  {{ item.description }}
                </p>

                <div class="item-meta">
                  <span class="category-tag">{{ getCategoryName(item.category_id) }}</span>
                  <span v-if="item.prep_time_minutes" class="prep-time">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                    </svg>
                    {{ item.prep_time_minutes }} min
                  </span>
                </div>

                <div class="item-actions">
                  <button
                    @click="toggleItemAvailability(item)"
                    :class="['btn-toggle', { available: item.available }]"
                  >
                    {{ item.available ? 'Mark Unavailable' : 'Mark Available' }}
                  </button>
                  <button @click="editMenuItem(item)" class="btn-icon" title="Edit">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                    </svg>
                  </button>
                  <button @click="deleteMenuItem(item.id)" class="btn-icon btn-danger" title="Delete">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Add/Edit Menu Item Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ editingItem ? 'Edit Menu Item' : 'Add New Menu Item' }}</h3>
          <button @click="closeModal" class="modal-close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Item Name (English) *</label>
              <input
                v-model="itemForm.name"
                type="text"
                placeholder="e.g., Margherita Pizza"
                class="form-input"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label">Item Name (Arabic)</label>
              <input
                v-model="itemForm.name_ar"
                type="text"
                placeholder="e.g., بيتزا مارجريتا"
                class="form-input"
                dir="rtl"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Category *</label>
            <select v-model="itemForm.category_id" class="form-select" required>
              <option :value="null">Select a category...</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Price (EGP) *</label>
              <input
                v-model.number="itemForm.price"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="form-input"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label">Prep Time (minutes)</label>
              <input
                v-model.number="itemForm.prep_time_minutes"
                type="number"
                placeholder="30"
                class="form-input"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Description (English)</label>
            <textarea
              v-model="itemForm.description"
              placeholder="Describe your delicious item..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">Description (Arabic)</label>
            <textarea
              v-model="itemForm.description_ar"
              placeholder="صف عنصرك اللذيذ..."
              class="form-textarea"
              rows="3"
              dir="rtl"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">Image URL *</label>
            <input
              v-model="itemForm.image_url"
              type="text"
              placeholder="https://example.com/image.jpg"
              class="form-input"
              required
            />
            <small class="form-hint">Image must be 1:1 or 3:2 ratio</small>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="itemForm.available"
                type="checkbox"
                class="form-checkbox"
              />
              <span>Available</span>
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn-secondary">Cancel</button>
          <button @click="saveMenuItem" class="btn-primary" :disabled="saving">
            {{ saving ? 'Saving...' : (editingItem ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'

export default {
  name: 'RestaurantMenuItemsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: false,
      saving: false,
      showAddModal: false,
      editingItem: null,
      categories: [],
      menuItems: [],
      filters: {
        categoryId: null,
        available: null
      },
      itemForm: {
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: 0,
        category_id: null,
        image_url: '',
        available: true,
        prep_time_minutes: 30
      }
    }
  },
  async mounted () {
    await this.checkRestaurantMode()
    this.loadCategories()
    this.loadMenuItems()
  },
  methods: {
    async checkRestaurantMode () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant/mode/check', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          authStore.setRestaurantMode(data.restaurant_mode || false)
        }
      } catch (error) {
        console.error('Error checking restaurant mode:', error)
      }
    },

    async loadCategories () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/categories', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.categories = data.categories || []
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },

    async loadMenuItems () {
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')
        let url = 'https://api.quali-d.com/restaurant-mode/menu-items'

        const params = new URLSearchParams()
        if (this.filters.categoryId) params.append('category_id', this.filters.categoryId)
        if (this.filters.available !== null) params.append('available', this.filters.available)

        if (params.toString()) {
          url += '?' + params.toString()
        }

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.menuItems = data.items || []
        }
      } catch (error) {
        console.error('Error loading menu items:', error)
        alert('Failed to load menu items')
      } finally {
        this.loading = false
      }
    },

    async saveMenuItem () {
      if (!this.itemForm.name.trim() || !this.itemForm.category_id || !this.itemForm.price) {
        alert('Please fill in all required fields')
        return
      }

      try {
        this.saving = true
        const token = localStorage.getItem('auth_token')
        const url = this.editingItem
          ? `https://api.quali-d.com/restaurant-mode/menu-items/${this.editingItem.id}`
          : 'https://api.quali-d.com/restaurant-mode/menu-items'

        const response = await fetch(url, {
          method: this.editingItem ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.itemForm)
        })

        const data = await response.json()
        if (data.success) {
          alert('Menu item saved successfully!')
          this.closeModal()
          this.loadMenuItems()
        } else {
          alert('Failed to save menu item: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error saving menu item:', error)
        alert('Failed to save menu item')
      } finally {
        this.saving = false
      }
    },

    editMenuItem (item) {
      this.editingItem = item
      this.itemForm = { ...item }
      this.showAddModal = true
    },

    async toggleItemAvailability (item) {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`https://api.quali-d.com/restaurant-mode/menu-items/${item.id}/toggle-availability`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        })

        const data = await response.json()
        if (data.success) {
          item.available = !item.available
          alert('Availability updated!')
        } else {
          alert('Failed to update availability: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error toggling availability:', error)
        alert('Failed to update availability')
      }
    },

    async deleteMenuItem (id) {
      if (!confirm('Are you sure you want to delete this menu item?')) return

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`https://api.quali-d.com/restaurant-mode/menu-items/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })

        const data = await response.json()
        if (data.success) {
          alert('Menu item deleted successfully!')
          this.loadMenuItems()
        } else {
          alert('Failed to delete menu item: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error deleting menu item:', error)
        alert('Failed to delete menu item')
      }
    },

    getCategoryName (categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      return category ? category.name : 'Unknown'
    },

    closeModal () {
      this.showAddModal = false
      this.editingItem = null
      this.itemForm = {
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: 0,
        category_id: null,
        image_url: '',
        available: true,
        prep_time_minutes: 30
      }
    }
  }
}
</script>

<style scoped>
.restaurant-menu-items-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.restaurant-menu-items-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.header-content {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  flex: 1;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
}

/* Filters */
.filters-section {
  background: var(--bg-primary);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  display: flex;
  gap: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
}

.filter-group {
  flex: 1;
  max-width: 300px;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}

.filter-select {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s;
}

.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: var(--text-primary);
}

.empty-state p {
  margin: 0 0 24px 0;
  font-size: 14px;
}

/* Menu Items Grid */
.menu-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

.menu-item-card {
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s;
}

.menu-item-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.item-image {
  width: 100%;
  height: 220px;
  position: relative;
  overflow: hidden;
  background: var(--bg-tertiary);
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.availability-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: #ef4444;
  color: white;
}

.availability-badge.available {
  background: #10b981;
}

.item-content {
  padding: 16px;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
}

.item-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  flex: 1;
}

.item-price {
  font-size: 20px;
  font-weight: 700;
  color: #3b82f6;
  margin-left: 12px;
}

.item-description {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);
}

.category-tag {
  padding: 4px 12px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.prep-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-toggle {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.btn-toggle.available {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.btn-toggle:hover {
  opacity: 0.8;
}

.btn-icon {
  background: none;
  border: none;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: var(--bg-tertiary);
  color: #3b82f6;
}

.btn-icon.btn-danger:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Buttons */
.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--border-primary);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-content.large {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 2px solid var(--border-primary);
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 2px solid var(--border-primary);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Form Elements */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  font-family: inherit;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;
  color: var(--text-primary);
}

.form-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .filters-section {
    flex-direction: column;
  }

  .filter-group {
    max-width: 100%;
  }

  .menu-items-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
